import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveStockReceipt,
  usePendingStockReceipts,
} from "../hooks/useApprovals";
import type { Batch } from "../services/batchesApi";

const unitLabels: Record<number, string> = {
  0: "units",
  1: "kg",
  2: "g",
  3: "L",
  4: "ml",
  5: "pcs",
  6: "boxes",
  7: "bags",
  8: "packets",
  9: "bottles",
  10: "cans",
  11: "trays",
  12: "seeds",
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatNumber = (value?: number | null, maximumFractionDigits = 4) => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-KE", { maximumFractionDigits });
};

const formatMoney = (value?: number | null, maximumFractionDigits = 2) => {
  if (value === null || value === undefined) return "—";
  return `KES ${value.toLocaleString("en-KE", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits,
  })}`;
};

const getInventoryUnit = (batch: Batch) => {
  if (typeof batch.unitOfMeasure === "number") {
    return unitLabels[batch.unitOfMeasure] ?? "units";
  }

  if (typeof batch.unitOfMeasure === "string" && batch.unitOfMeasure.trim()) {
    return batch.unitOfMeasure;
  }

  return "units";
};

const pluralize = (unit: string, quantity?: number | null) => {
  if (quantity === 1) return unit;
  const normalized = unit.toLowerCase();
  if (normalized === "pack") return "packs";
  if (normalized === "packet") return "packets";
  if (normalized.endsWith("s")) return unit;
  return `${unit}s`;
};

const getCommercialQuantityLabel = (batch: Batch) => {
  const quantity = batch.commercialQuantity ?? batch.numberOfPacks;
  if (!quantity) return null;

  const unit = batch.commercialUnit || "pack";
  return `${formatNumber(quantity)} ${pluralize(unit, quantity)}`;
};

const getPackDetailLabel = (batch: Batch) => {
  if (!batch.numberOfPacks || !batch.packSize) return null;
  return `${formatNumber(batch.numberOfPacks)} x ${formatNumber(batch.packSize)} ${getInventoryUnit(batch)}`;
};

export const StockReceiptApprovalsPage = () => {
  const notify = useNotification();
  const pendingQuery = usePendingStockReceipts();
  const approveMutation = useApproveStockReceipt();

  const [selected, setSelected] = useState<Batch | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleApprove = (b: Batch) => {
    approveMutation.mutate(
      { id: b.id, request: { isApproved: true } },
      {
        onSuccess: () =>
          notify.success(`Stock receipt ${b.batchNumber} approved — stock is now drawable.`),
        onError: () => notify.error("Failed to approve stock receipt."),
      }
    );
  };

  const openReject = (b: Batch) => {
    setSelected(b);
    setRejectOpen(true);
  };

  const handleReject = (reason: string) => {
    if (!selected) return;
    approveMutation.mutate(
      { id: selected.id, request: { isApproved: false, rejectionReason: reason } },
      {
        onSuccess: () => {
          notify.success(`Stock receipt ${selected.batchNumber} rejected.`);
          setRejectOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to reject stock receipt."),
      }
    );
  };

  const rows = pendingQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Stock Receipt Approvals</h1>
        <p className="mt-1 text-muted-foreground">
          Review stock receipts awaiting GM approval. Pending batches show with a
          badge in inventory and are not drawable until approved.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="inventory.approve_stock_receipt" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending Stock Receipts</CardTitle>
            {rows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                {rows.length}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No stock receipts awaiting approval.
            </p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch #</TableHead>
                    <TableHead>Input</TableHead>
                    <TableHead>Received Qty</TableHead>
                    <TableHead>Costing</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Received On</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((b) => {
                    const commercialQuantity = getCommercialQuantityLabel(b);
                    const inventoryUnit = getInventoryUnit(b);
                    const packDetail = getPackDetailLabel(b);
                    const commercialUnit = b.commercialUnit || "pack";
                    const hasCommercialCost =
                      b.commercialQuantity !== null &&
                      b.commercialQuantity !== undefined &&
                      b.commercialUnitCost !== null &&
                      b.commercialUnitCost !== undefined;

                    return (
                      <TableRow key={b.id}>
                        <TableCell className="font-medium">{b.batchNumber}</TableCell>
                        <TableCell className="text-sm">
                          {b.inputName || "—"}
                          {b.inputCode && (
                            <span className="text-xs text-muted-foreground block">
                              {b.inputCode}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-semibold">
                            {commercialQuantity ?? `${formatNumber(b.receivedQuantity)} ${inventoryUnit}`}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Inventory: {formatNumber(b.receivedQuantity)} {inventoryUnit}
                            {packDetail ? ` (${packDetail})` : ""}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {hasCommercialCost ? (
                            <>
                              <div className="font-medium">
                                {formatMoney(b.commercialUnitCost)} / {commercialUnit.toLowerCase()}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Total: {formatMoney(b.commercialSubtotal)}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="font-medium">
                                {formatMoney(b.unitCost, 6)} / {inventoryUnit}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                No commercial quantity captured
                              </div>
                            </>
                          )}
                          {b.costingReviewRequired && (
                            <Badge variant="outline" className="mt-1 border-orange-300 text-orange-700">
                              Review
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{b.supplierName || "—"}</TableCell>
                        <TableCell className="text-sm">{formatDate(b.receivedDate)}</TableCell>
                        <TableCell className="text-sm">{formatDate(b.expiryDate)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(b)}
                              disabled={approveMutation.isPending}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => openReject(b)}
                              disabled={approveMutation.isPending}
                            >
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Stock Receipt"
        description="The receiver will be notified with this reason and can record a corrected receipt."
        reasonLabel="Rejection Reason"
        reasonPlaceholder="e.g. Quantity mismatch with delivery note"
        submitLabel="Reject"
        isSubmitting={approveMutation.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
